import { Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react'
import { 
  MapPin, 
  DollarSign, 
  Calendar, 
  Clock,
  CheckCircle,
  Building,
  Users,
  FileText,
  AlertCircle,
  ExternalLink,
  Send,
  X,
  Briefcase,
  Globe,
  ListChecks,
  Sparkles,
  BarChart3,
  GraduationCap,
  Eye
} from 'lucide-react'
import { apiRequest } from '@/config/api'
import ApplicationForm from '@/components/candidates/ApplicationForm'
import ApplicationStatus from '@/components/candidates/ApplicationStatus'
import CandidateHeader from '@/components/common/CandidateHeader'
import JobSkillMatch from '@/components/jobs/JobSkillMatch'

interface PublicPosition {
  uuid: string
  title: string
  description: string
  short_description?: string
  department_name?: string
  primary_location?: string
  employment_type: string
  salary_min?: number
  salary_max?: number
  salary_currency?: string
  salary_frequency?: string
  application_deadline?: string
  required_skills?: any
  preferred_skills?: any
  tags?: any
  skills_required?: any
  required_experience_years?: number
  total_openings?: number
  filled_positions?: number
  company_name: string
  company_description?: string
  company_website?: string
  company_industry?: string
  company_size?: string
  created_at: string
  experience_level?: string
  remote_type?: string
  education_requirements?: string
  expected_start_date?: string
  application_count?: number
  view_count?: number
}

export default function PublicJobPage() {
  const params = useParams()
  const [position, setPosition] = useState<PublicPosition | null>(null)
  const [positionSkills, setPositionSkills] = useState<any[]>([])
  const [positionCertifications, setPositionCertifications] = useState<any[]>([])
  const [skillsLoading, setSkillsLoading] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showApplicationForm, setShowApplicationForm] = useState(false)
  const [authenticatedEmail, setAuthenticatedEmail] = useState('')
  const [showApplicationStatus, setShowApplicationStatus] = useState(false)
  const [existingApplicationId, setExistingApplicationId] = useState<number | null>(null)
  const [isLoggedIn, setIsLoggedIn] = useState(false)

  const navigate = useNavigate()
  const location = useLocation()
  const locationState = (location.state as { fromApplications?: boolean; applicationStatus?: string }) || {}
  const cameFromApplications = Boolean(locationState.fromApplications)
  const applicationStatusFromList = locationState.applicationStatus

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
    
    loadPublicPosition()
    loadPositionSkills()
  }, [params.id])

  const loadPublicPosition = async () => {
    try {
      setLoading(true)
      setError('')
      
      const response = await apiRequest(`jobs/${params.id}`)
      const data = await response.json()
      
      if (response.ok && data.success) {
        setPosition(data.data.job)
      } else {
        setError(data.error || 'Position not found or not available for public viewing')
      }
    } catch (err: any) {
      console.error('Public position load error:', err)
      setError('Failed to load job posting. This position may not be publicly available.')
    } finally {
      setLoading(false)
    }
  }

  const loadPositionSkills = async () => {
    try {
      setSkillsLoading(true)
      
      const response = await apiRequest(`jobs/${params.id}/skills`)
      const data = await response.json()
      
      if (response.ok && data.success) {
        setPositionSkills(data.data.skills || [])
        setPositionCertifications(data.data.certifications || [])
      } else {
        console.log('No skills found for this position:', data.message)
      }
    } catch (err: any) {
      console.error('Position skills load error:', err)
    } finally {
      setSkillsLoading(false)
    }
  }

  const formatSalary = (min?: number | null, max?: number | null, currency: string = 'USD', frequency?: string) => {
    const hasMin = typeof min === 'number' && !Number.isNaN(min)
    const hasMax = typeof max === 'number' && !Number.isNaN(max)
    
    if (!hasMin && !hasMax) return 'Salary not specified'
    const cadence = formatSalaryCadence(frequency)
    
    if (hasMin && hasMax) {
      return `${currency} ${(min as number).toLocaleString()} - ${(max as number).toLocaleString()}${cadence}`
    }
    if (hasMin) return `${currency} ${(min as number).toLocaleString()}+${cadence}`
    if (hasMax) return `Up to ${currency} ${(max as number).toLocaleString()}${cadence}`
    return `Competitive salary${cadence}`
  }

  const formatSalaryCadence = (frequency?: string | null) => {
    if (!frequency) return ''
    const normalized = frequency.replace(/_/g, ' ').toLowerCase()
    if (normalized.includes('year')) return ' per year'
    if (normalized.includes('month')) return ' per month'
    if (normalized.includes('week')) return ' per week'
    if (normalized.includes('day')) return ' per day'
    if (normalized.includes('hour')) return ' per hour'
    return ` per ${normalized}`
  }

  const formatLabel = (value?: string | null) => {
    if (!value) return ''
    return value
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const parseSkillEntry = (entry: any): string | null => {
    if (!entry) return null
    if (typeof entry === 'string') return entry
    if (typeof entry === 'object') {
      const name = entry.skill_name || entry.name || entry.skill || entry.title
      const level = entry.proficiency_level || entry.level
      if (name) {
        return level ? `${name} (${level})` : name
      }
    }
    return null
  }

  const normalizeSkillList = (skills: any): string[] => {
    if (!skills) return []

    if (Array.isArray(skills)) {
      return skills
        .map(parseSkillEntry)
        .filter((skill): skill is string => Boolean(skill))
        .map(skill => skill.trim())
        .filter(Boolean)
    }

    if (typeof skills === 'string') {
      const trimmed = skills.trim()
      try {
        const parsed = JSON.parse(trimmed)
        if (Array.isArray(parsed)) {
          return parsed
            .map(parseSkillEntry)
            .filter((skill): skill is string => Boolean(skill))
            .map(skill => skill.trim())
            .filter(Boolean)
        }
      } catch (_) {
        // Not JSON - fall through to delimiter-based parsing
      }

      return trimmed
        .split(/[\n,;•]+/)
        .map(item => item.replace(/^[-•\s]+/, '').trim())
        .filter(Boolean)
    }

    if (typeof skills === 'object') {
      return Object.values(skills)
        .map(parseSkillEntry)
        .filter((skill): skill is string => Boolean(skill))
        .map(skill => skill.trim())
        .filter(Boolean)
    }

    return []
  }

  const normalizePositionSkills = (skills: any): { skill_name: string; is_required: boolean; proficiency_level?: string | null; skill_id?: number; category?: string | null; skill_category?: string | null }[] => {
    if (!skills) return []

    const normalize = (item: any, index: number) => {
      if (!item) return null
      if (typeof item === 'string') {
        const name = item.trim()
        if (!name) return null
        return { skill_name: name, is_required: true, proficiency_level: undefined, skill_id: index, category: 'General', skill_category: 'General' }
      }
      if (typeof item === 'object') {
        const name = item.skill_name || item.name || item.skill || ''
        if (!name) return null
        return {
          skill_name: name,
          is_required: item.is_required ?? !item.is_good_to_have ?? true,
          proficiency_level: item.proficiency_level || item.level,
          skill_id: item.skill_id || item.id || index,
          category: item.category || item.skill_category || item.type || 'General',
          skill_category: item.skill_category || item.category || item.type || 'General'
        }
      }
      return null
    }

    if (Array.isArray(skills)) {
      return skills.map(normalize).filter((s): s is NonNullable<typeof s> => Boolean(s))
    }

    if (typeof skills === 'string') {
      const trimmed = skills.trim()
      try {
        const parsed = JSON.parse(trimmed)
        if (Array.isArray(parsed)) {
          return parsed.map(normalize).filter((s): s is NonNullable<typeof s> => Boolean(s))
        }
      } catch (_) {
        // fallthrough
      }
      return trimmed
        .split(/[\n,;]+/)
        .map(item => item.replace(/^[-•\s]+/, '').trim())
        .filter(Boolean)
        .map((name, idx) => normalize(name, idx))
        .filter((s): s is NonNullable<typeof s> => Boolean(s))
    }

    return []
  }

  const isDeadlinePassed = (deadline: string) => {
    return new Date(deadline) < new Date()
  }

  const redirectToLogin = () => {
    const currentUrl = `${window.location.pathname}${window.location.search}`
    navigate(`/login?returnUrl=${encodeURIComponent(currentUrl)}`)
  }

  const handleApplyClick = async () => {
    if (!isLoggedIn) {
      redirectToLogin()
      return
    }

    const token = localStorage.getItem('token');
    if (!token) {
      redirectToLogin()
      return
    }

    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const userEmail = payload.email || payload.sub || '';
      
      if (!userEmail || !position) {
        redirectToLogin()
        return
      }

      const hasAppliedAlready = await checkEmailApplicationStatus(userEmail, position.uuid);
      if (!hasAppliedAlready) {
        setAuthenticatedEmail(userEmail);
        setShowApplicationForm(true);
      }
    } catch (error) {
      console.error('Error parsing token:', error);
      redirectToLogin()
    }
  }

  const checkEmailApplicationStatus = async (email: string, positionUuid: string): Promise<boolean> => {
    try {
      const response = await apiRequest('applicants/check-application-status', {
        method: 'POST',
        body: JSON.stringify({
          email: email,
          position_uuid: positionUuid
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        const applicationData = data.data
        
        if (applicationData.has_applied) {
          setExistingApplicationId(applicationData.application_id)
          setShowApplicationStatus(true)
          return true
        }
      }
      
      return false
    } catch (err: any) {
      console.error('Error checking application status:', err)
      return false
    }
  }

  const handleApplicationSuccess = () => {
    setShowApplicationForm(false)
    // Redirect to candidate dashboard to track applications
    setTimeout(() => {
      window.location.href = '/candidate/dashboard'
    }, 2000)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        {isLoggedIn && <CandidateHeader />}
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading job posting...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !position) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        {isLoggedIn && <CandidateHeader />}
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center max-w-md">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Job Not Available</h1>
            <p className="text-gray-600 mb-6">
              {error || 'This job posting is no longer available or has been set to private.'}
            </p>
            <Link
              to="/"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              Browse Other Opportunities
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const deadlinePassed = position.application_deadline ? isDeadlinePassed(position.application_deadline) : false
  const requiredSkills = normalizeSkillList(position.required_skills)
  const preferredSkills = normalizeSkillList(position.preferred_skills)
  const positionSkillsFromJob = normalizePositionSkills(position.skills_required)
  const normalizedApiSkills = (positionSkills || []).map((skill, index) => ({
    ...skill,
    category: (skill as any).category || (skill as any).skill_category || 'General',
    skill_id: (skill as any).skill_id || index
  }))
  const tagList = normalizeSkillList(position.tags)
  const totalOpenings = position.total_openings ?? null
  const filledPositions = position.filled_positions ?? 0
  const openingsRemaining = totalOpenings !== null ? Math.max(0, totalOpenings - filledPositions) : null
  // Prefer skills from position payload (skills_required) to preserve categories; fallback to skills endpoint
  const displayedPositionSkills = positionSkillsFromJob.length > 0
    ? positionSkillsFromJob
    : (normalizedApiSkills && normalizedApiSkills.length > 0 ? normalizedApiSkills : [])

  const getSkillCategory = (skill: any) => {
    return skill?.category || skill?.skill_category || 'General'
  }

  const groupSkillsByCategory = (skills: any[]) => {
    return skills.reduce((acc: Record<string, any[]>, skill) => {
      const category = getSkillCategory(skill)
      if (!acc[category]) acc[category] = []
      acc[category].push(skill)
      return acc
    }, {})
  }
  const jobHighlights = [
    position.experience_level ? {
      label: 'Experience Level',
      value: formatLabel(position.experience_level),
      icon: Briefcase
    } : null,
    typeof position.required_experience_years === 'number' ? {
      label: 'Minimum Experience',
      value: `${position.required_experience_years}+ years`,
      icon: BarChart3
    } : null,
    position.remote_type ? {
      label: 'Work Model',
      value: formatLabel(position.remote_type),
      icon: Globe
    } : null,
    position.salary_frequency ? {
      label: 'Pay Schedule',
      value: formatLabel(position.salary_frequency),
      icon: DollarSign
    } : null,
    (totalOpenings !== null || filledPositions) ? {
      label: 'Open Positions',
      value: totalOpenings !== null 
        ? `${openingsRemaining ?? 0} of ${totalOpenings} open`
        : `${openingsRemaining ?? 0} roles available`,
      icon: Users
    } : null,
    typeof position.application_count === 'number' ? {
      label: 'Applications',
      value: `${position.application_count} applicant${position.application_count === 1 ? '' : 's'}`,
      icon: BarChart3
    } : null,
    typeof position.view_count === 'number' ? {
      label: 'Views',
      value: `${position.view_count.toLocaleString()} views`,
      icon: Eye
    } : null,
    position.education_requirements ? {
      label: 'Education',
      value: position.education_requirements,
      icon: GraduationCap
    } : null
  ].filter(Boolean) as { label: string; value: string; icon: any }[]

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {isLoggedIn && <CandidateHeader />}
      
      {/* Header - Only show if not logged in */}
      {!isLoggedIn && (
        <div className="bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
                <Building className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-sm font-medium text-gray-500">Career Opportunity</h1>
                <p className="text-lg font-semibold text-gray-900">{position.company_name || 'Company'}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ${isLoggedIn ? 'pb-20' : ''}`}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Job Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Job Header */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="mb-4">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{position.title}</h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                  {position.department_name && (
                    <span className="flex items-center">
                      <Users className="w-4 h-4 mr-1" />
                      {position.department_name}
                    </span>
                  )}
                  {position.primary_location && (
                    <span className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      {position.primary_location}
                    </span>
                  )}
                  {position.employment_type && (
                    <span className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {position.employment_type.replace('_', ' ').split(' ').map(word => 
                        word.charAt(0).toUpperCase() + word.slice(1)
                      ).join(' ')}
                    </span>
                  )}
                  {(position.salary_min || position.salary_max) && (
                    <span className="flex items-center">
                      <DollarSign className="w-4 h-4 mr-1" />
                      {formatSalary(position.salary_min, position.salary_max, position.salary_currency, position.salary_frequency)}
                    </span>
                  )}
                </div>
              </div>

              {/* Application Status */}
              {position.application_deadline && (
                <div className={`flex items-center space-x-2 p-3 rounded-lg ${
                  deadlinePassed 
                    ? 'bg-red-50 border border-red-200' 
                    : 'bg-green-50 border border-green-200'
                }`}>
                  {deadlinePassed ? (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  )}
                  <span className={`text-sm font-medium ${
                    deadlinePassed ? 'text-red-700' : 'text-green-700'
                  }`}>
                    {deadlinePassed 
                      ? 'Application deadline has passed' 
                      : `Apply by ${new Date(position.application_deadline).toLocaleDateString()}`
                    }
                  </span>
                </div>
              )}
            </div>

            {jobHighlights.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Role Highlights</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {jobHighlights.map(({ label, value, icon: Icon }) => (
                    <div key={label} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50">
                      <div className="p-2 rounded-lg bg-blue-100 text-blue-700">
                        <Icon className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
                        <p className="text-sm font-semibold text-gray-900 mt-1">{value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Job Description */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-blue-600" />
                About This Role
              </h2>
              <div className="prose prose-sm max-w-none text-gray-700">
                {position.short_description && (
                  <p className="text-gray-700 mb-3">{position.short_description}</p>
                )}
                {position.description ? (
                  <div className="whitespace-pre-wrap">{position.description}</div>
                ) : (
                  <p className="text-gray-500 italic">No description provided</p>
                )}
              </div>
            </div>

            {/* Requirements */}
            {(requiredSkills.length > 0 || preferredSkills.length > 0 || tagList.length > 0 || position.education_requirements) && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                  What We're Looking For
                </h2>
                
                {requiredSkills.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-2">
                      <ListChecks className="w-4 h-4 text-blue-600" />
                      <h3 className="text-lg font-medium text-gray-900">Role Requirements</h3>
                    </div>
                    <ul className="list-disc list-inside space-y-2 text-gray-800">
                      {requiredSkills.map((item, index) => (
                        <li key={`required-${index}`} className="leading-relaxed">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {preferredSkills.length > 0 && (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-purple-600" />
                      <h3 className="text-lg font-medium text-gray-900">Nice to have skills</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {preferredSkills.map((skill, index) => (
                        <span 
                          key={`preferred-${index}`}
                          className="px-3 py-1 bg-purple-100 text-purple-800 text-sm font-medium rounded-full"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {position.education_requirements && (
                  <div className="mb-6">
                    <div className="flex items-center gap-2 mb-2">
                      <GraduationCap className="w-4 h-4 text-indigo-600" />
                      <h3 className="text-lg font-medium text-gray-900">Education</h3>
                    </div>
                    <p className="text-sm text-gray-700">{position.education_requirements}</p>
                  </div>
                )}

                {tagList.length > 0 && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Keywords</h3>
                    <div className="flex flex-wrap gap-2">
                      {tagList.map((tag, index) => (
                        <span 
                          key={`tag-${index}`}
                          className="px-3 py-1 bg-gray-100 text-gray-800 text-sm font-medium rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {(requiredSkills.length === 0 && preferredSkills.length === 0 && !position.education_requirements && tagList.length === 0) && (
                  <p className="text-sm text-gray-500">The hiring team hasn't listed specific requirements for this role.</p>
                )}
              </div>
            )}

            {/* Skills & Skill Match */}
            {displayedPositionSkills.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2 text-blue-600" />
                  Skills Required
                </h2>

                {/* Grouped by category */}
                <div className="space-y-6">
                  {Object.entries(groupSkillsByCategory(displayedPositionSkills)).map(([category, skills]) => (
                    <div key={category} className="space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-semibold border border-blue-200">
                          {category}
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {skills.map((skill: any, index: number) => (
                          <span
                            key={`${category}-${index}`}
                            className={`px-3 py-1 text-sm font-medium rounded-full border ${
                              skill.is_required ? 'bg-red-50 text-red-800 border-red-200' : 'bg-blue-50 text-blue-800 border-blue-200'
                            }`}
                          >
                            <span className="font-semibold">{skill.skill_name}</span>
                            {skill.proficiency_level && ` (${skill.proficiency_level})`}
                            {!skill.is_required && <span className="ml-2 text-xs">(Nice to have)</span>}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Skill Match for logged-in users */}
                {isLoggedIn && (
                  <div className="mt-6">
                    <JobSkillMatch 
                      positionSkills={displayedPositionSkills}
                      positionCertifications={positionCertifications}
                      className="w-full"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Info */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Job Details</h3>
              <dl className="space-y-3">
                {position.department_name && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Department</dt>
                    <dd className="mt-1 text-sm text-gray-900">{position.department_name}</dd>
                  </div>
                )}

                {position.primary_location && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Location</dt>
                    <dd className="mt-1 text-sm text-gray-900">{position.primary_location}</dd>
                  </div>
                )}

                {position.employment_type && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Employment Type</dt>
                    <dd className="mt-1 text-sm text-gray-900 capitalize">
                      {position.employment_type.replace('_', ' ')}
                    </dd>
                  </div>
                )}

                {position.experience_level && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Experience Level</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formatLabel(position.experience_level)}</dd>
                  </div>
                )}

                {position.remote_type && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Work Model</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formatLabel(position.remote_type)}</dd>
                  </div>
                )}

                {typeof position.required_experience_years === 'number' && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Minimum Experience</dt>
                    <dd className="mt-1 text-sm text-gray-900">{position.required_experience_years}+ years</dd>
                  </div>
                )}

                {(position.salary_min || position.salary_max) && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Salary</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {formatSalary(position.salary_min, position.salary_max, position.salary_currency, position.salary_frequency)}
                    </dd>
                  </div>
                )}

                {position.application_deadline && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Application Deadline</dt>
                    <dd className={`mt-1 text-sm ${deadlinePassed ? 'text-red-600 font-medium' : 'text-gray-900'}`}>
                      {new Date(position.application_deadline).toLocaleDateString()}
                    </dd>
                  </div>
                )}

                {(totalOpenings !== null || filledPositions) && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Open Positions</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {openingsRemaining !== null ? `${openingsRemaining} open` : 'Openings available'}
                      {totalOpenings !== null ? ` of ${totalOpenings}` : ''}
                    </dd>
                  </div>
                )}

                {typeof position.application_count === 'number' && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Applications</dt>
                    <dd className="mt-1 text-sm text-gray-900">{position.application_count} received</dd>
                  </div>
                )}

                {typeof position.view_count === 'number' && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Views</dt>
                    <dd className="mt-1 text-sm text-gray-900">{position.view_count.toLocaleString()}</dd>
                  </div>
                )}

                <div>
                  <dt className="text-sm font-medium text-gray-500">Posted</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {new Date(position.created_at).toLocaleDateString()}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Apply Section */}
            {!deadlinePassed && (
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl shadow-sm p-6 text-white">
                <h3 className="text-lg font-semibold mb-3">
                  {cameFromApplications ? 'Application Submitted' : 'Ready to Apply?'}
                </h3>
                <p className="text-blue-100 text-sm mb-4">
                  {cameFromApplications 
                    ? 'You already applied to this role from your applications list.'
                    : 'Submit your application directly through our platform.'
                  }
                </p>
                {cameFromApplications ? (
                  <div className="w-full bg-white/10 border border-white/30 text-white font-semibold py-3 px-4 rounded-lg flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="w-4 h-4 text-green-200" />
                      <span>Already applied</span>
                    </div>
                    {applicationStatusFromList && (
                      <span className="text-sm px-3 py-1 rounded-full bg-white text-blue-700">
                        {formatLabel(applicationStatusFromList)}
                      </span>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={handleApplyClick}
                    className="w-full bg-white text-blue-600 hover:bg-blue-50 font-medium py-3 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2"
                  >
                    <Send className="w-4 h-4" />
                    <span>Apply Now</span>
                  </button>
                )}
              </div>
            )}

            {deadlinePassed && (
              <div className="bg-gray-100 rounded-xl shadow-sm p-6">
                <div className="text-center">
                  <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Applications Closed</h3>
                  <p className="text-sm text-gray-600">
                    The application deadline for this position has passed.
                  </p>
                  {cameFromApplications && (
                    <p className="text-sm text-gray-700 mt-3 font-medium">
                      You already applied for this position.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Company Info */}
            {position.company_name && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
                  <Building className="w-5 h-5 mr-2 text-blue-600" />
                  About the Company
                </h3>
                <p className="text-sm text-gray-700">
                  {position.company_description || `Join ${position.company_name} and be part of an innovative team making a difference.`}
                </p>

                <div className="mt-4 space-y-2 text-sm text-gray-700">
                  {position.company_industry && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Industry</span>
                      <span className="font-medium text-gray-900">{position.company_industry}</span>
                    </div>
                  )}
                  {position.company_size && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Company Size</span>
                      <span className="font-medium text-gray-900">{position.company_size}</span>
                    </div>
                  )}
                  {position.primary_location && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500">Headquarters</span>
                      <span className="font-medium text-gray-900">{position.primary_location}</span>
                    </div>
                  )}
                </div>

                {position.company_website && (
                  <a 
                    href={position.company_website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    Visit company website
                    <ExternalLink className="w-4 h-4 ml-1" />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Application Form Modal */}
      {showApplicationForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Apply for Position</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {position.title} at {position.company_name}
                </p>
              </div>
              <button
                onClick={() => setShowApplicationForm(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <ApplicationForm 
                position={position} 
                onSuccess={handleApplicationSuccess}
                authenticatedEmail={authenticatedEmail}
              />
            </div>
          </div>
        </div>
      )}

      {/* Application Status Modal */}
      {showApplicationStatus && existingApplicationId && position && (
        <ApplicationStatus
          applicationId={existingApplicationId}
          positionTitle={position.title}
          companyName={position.company_name}
          onClose={() => {
            setShowApplicationStatus(false)
            setExistingApplicationId(null)
          }}
        />
      )}
    </div>
  )
}
